import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { createClient, createParent } from "../store/thunks/clientsThunk";
import GoogleMapsAutocomplete from "../applicationUi/components/GoogleAutoComplete";
import ProvinceSelector from "../applicationUi/components/ProvinceSelector";
import GenderSelector from "../applicationUi/components/GenderSelector";
import PronounSelector from "../applicationUi/components/PronounSelector";
import { Switch } from "@headlessui/react";
import SearchDropdown from "../applicationUi/components/SearchDropdown";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function formatPhoneNumber(phoneNumber) {
  // Remove all non-digit characters
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");

  // Check if the input is of correct length
  if (cleaned.length !== 10) {
    return phoneNumber; // Return the original if it's not 10 digits
  }

  // Format the cleaned number
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : phoneNumber;
}

export default function QuickAddClientModal({ onClose, addParent = false }) {
  const dispatch = useDispatch();
  const modalRef = React.useRef(null);
  const formRef = React.useRef(null);
  const [successState, setSuccessState] = useState(null);
  const [parentAddress, setParentAddress] = useState({
    aptUnit: "",
    streetNumber: "",
    streetName: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });
  const [clientAddress, setClientAddress] = useState({
    aptUnit: "",
    streetNumber: "",
    streetName: "",
    city: "",
    state: "",
    zipCode: "",
    country: "United States",
  });

  // Debug logging for address state changes
  useEffect(() => {
    console.log(`[QuickAddClientModal] Parent address state:`, parentAddress);
  }, [parentAddress]);

  useEffect(() => {
    console.log(`[QuickAddClientModal] Client address state:`, clientAddress);
  }, [clientAddress]);

  const [parentPhoneNumbers, setParentPhoneNumbers] = useState([
    { type: "", number: "" },
  ]);
  const [clientPhoneNumbers, setClientPhoneNumbers] = useState([
    { type: "", number: "" },
  ]);
  const [clientEmails, setClientEmails] = useState([]);
  const [parentEmails, setParentEmails] = useState([]);
  const [secondParentInfo, setSecondParentInfo] = useState({
    email: "",
    phone: "",
  });
  const [showParentInfo, setShowParentInfo] = useState(
    addParent ? true : false
  );
  const [treatingClinician, setTreatingClinician] = useState(false);
  const [supervisingClinician, setSupervisingClinician] = useState(false);
  const [errors, setErrors] = useState({});

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const scrollToBottom = () => {
    if (modalRef.current) {
      modalRef.current.scrollTo({
        top: modalRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const validateForm = (formData) => {
    const newErrors = {};

    // Only validate client fields if not in addParent mode
    if (!addParent) {
      // Required client fields
      const requiredClientFields = {
        clientFirstName: "First name",
        clientLastName: "Last name",
        clientDateOfBirth: "Date of birth",
        clientEmail: "Email",
      };

      Object.entries(requiredClientFields).forEach(([field, label]) => {
        if (!formData.get(field)) {
          newErrors[field] = `${label} is required`;
        }
      });

      // Validate client phone numbers
      if (!clientPhoneNumbers.some((phone) => phone.type && phone.number)) {
        newErrors.clientPhoneNumbers =
          "At least one phone number with type is required";
      } else {
        // Validate each phone number that has either type or number filled
        clientPhoneNumbers.forEach((phone, index) => {
          if ((phone.type && !phone.number) || (!phone.type && phone.number)) {
            newErrors[`clientPhoneNumber${index}`] =
              "Both phone type and number are required";
          }
        });
      }
    }

    // Validate parent fields if parent info is shown
    if (showParentInfo) {
      if (!formData.get("parentFirstName")) {
        newErrors.parentFirstName = "Parent first name is required";
      }
      if (!formData.get("parentLastName")) {
        newErrors.parentLastName = "Parent last name is required";
      }
      if (!parentPhoneNumbers.some((phone) => phone.type && phone.number)) {
        newErrors.parentPhoneNumbers =
          "At least one parent phone number with type is required";
      }
    }

    console.log(newErrors);

    setErrors(newErrors);

    // If there are errors, scroll to the bottom
    if (Object.keys(newErrors).length > 0) {
      setTimeout(scrollToBottom, 100); // Small delay to ensure DOM is updated
    }

    return Object.keys(newErrors).length === 0;
  };

  // Add this helper function to get all validation errors
  const getValidationErrors = () => {
    if (Object.keys(errors).length === 0) return null;

    const errorMessages = [];
    if (errors.clientFirstName) errorMessages.push("First name is required");
    if (errors.clientLastName) errorMessages.push("Last name is required");
    if (errors.clientDateOfBirth)
      errorMessages.push("Date of birth is required");
    if (errors.clientEmail) errorMessages.push("Email is required");
    if (errors.clientPhoneNumbers)
      errorMessages.push(errors.clientPhoneNumbers);

    if (errorMessages.length === 0) return null;

    return (
      <div className="my-4 p-4 bg-red-50 border border-red-200 rounded-sm">
        <div className="flex">
          <div className="flex-shrink-0">
            <XMarkIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Please fix the following errors:
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <ul role="list" className="list-disc list-inside">
                {errorMessages.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    if (!validateForm(formData)) {
      const firstError = document.querySelector(".error-field");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Collect parent data
    const parentData = {
      type: "parent",
      firstName: formData.get("parentFirstName"),
      lastName: formData.get("parentLastName"),
      dateOfBirth: formData.get("parentDateOfBirth"),
      email: formData.get("parentEmail"),
      additionalEmails: parentEmails,
      phoneNumbers: parentPhoneNumbers.map((phone) => ({
        ...phone,
        number: formatPhoneNumber(phone.number),
      })),
      address: parentAddress,
      relationship: formData.get("parentRelationship"),
      gender: formData.get("parentGender"),
      pronouns: formData.get("parentPronouns"),
      secondParentEmail: formData.get("secondParentEmail"),
      secondParentPhone: formData.get("secondParentPhone"),
    };

    // Collect client data
    const clientData = {
      type: "client",
      firstName: formData.get("clientFirstName"),
      lastName: formData.get("clientLastName"),
      dateOfBirth: formData.get("clientDateOfBirth"),
      email: formData.get("clientEmail"),
      additionalEmails: clientEmails,
      phoneNumbers: clientPhoneNumbers.map((phone) => ({
        ...phone,
        number: formatPhoneNumber(phone.number),
      })),
      parents: parentData.firstName && parentData.lastName ? [parentData] : [],
      address: clientAddress,
      insuranceId: formData.get("clientInsuranceId"),
      gender: formData.get("gender"),
      pronouns: formData.get("pronouns"),
      treatingClinician: {
        current: treatingClinician?._id,
        history: [{ clinician: treatingClinician?._id, date: new Date() }],
      },
      supervisingClinician: {
        current: supervisingClinician?._id,
        history: [{ clinician: supervisingClinician?._id, date: new Date() }],
      },
    };

    try {
      console.log("Dispatching createClient with data:", clientData);
      const result = await dispatch(createClient(clientData)).unwrap();
      console.log("Client created successfully:", result);
      setSuccessState({
        type: "client",
        id: result.client.clientId,
      });
    } catch (error) {
      console.error("Error saving client:", error);
      setErrors({
        submit: "An unexpected error occurred. Please try again.",
      });
      toast.error("Failed to create client. Please try again.");
    }
  };

  const handlePlaceSelected = (data, setAddress) => {
    if (data) {
      console.log(`[QuickAddClientModal] Google Maps Data Object:`, data);

      let newAddress = {};
      
      // Handle new API format with parsed data
      if (data.parsed) {
        console.log(`[QuickAddClientModal] Using pre-parsed address data:`, data.parsed);
        newAddress = {
          streetNumber: data.parsed.streetNumber || "",
          streetName: data.parsed.streetName || "",
          city: data.parsed.city || "",
          state: data.parsed.state || "",
          zipCode: data.parsed.zipCode || "",
          aptUnit: data.parsed.aptUnit || "",
        };
      } else {
        // Handle legacy format for backward compatibility
        const place = data;
        const addressComponents = place.address_components || place.addressComponents;
        
        if (addressComponents) {
          console.log(`[QuickAddClientModal] Address components found:`, addressComponents);
          addressComponents.forEach((component) => {
            const types = component.types || [];
            if (types.includes("street_number")) {
              newAddress.streetNumber = component.long_name || component.longText;
            }
            if (types.includes("route")) {
              newAddress.streetName = component.long_name || component.longText;
            }
            if (types.includes("locality")) {
              newAddress.city = component.long_name || component.longText;
            }
            if (types.includes("administrative_area_level_1")) {
              newAddress.state = component.short_name || component.shortText;
            }
            if (types.includes("postal_code")) {
              newAddress.zipCode = component.long_name || component.longText;
            }
            if (types.includes("subpremise")) {
              newAddress.aptUnit = component.long_name || component.longText;
            }
          });
        } else {
          console.warn(`[QuickAddClientModal] No address components found in place object`);
        }
      }
      
      console.log(`[QuickAddClientModal] Final Address Data:`, newAddress);
      
      setAddress((prev) => {
        const updatedAddress = {
          ...prev,
          ...newAddress,
        };
        console.log(`[QuickAddClientModal] Setting address state:`, updatedAddress);
        return updatedAddress;
      });
    } else {
      console.warn(`[QuickAddClientModal] No data object received`);
    }
  };

  const inputFields = [
    { name: "firstName", required: true },
    { name: "lastName", required: true },
    { name: "dateOfBirth", required: false },
    { name: "email", required: false },
    { name: "relationship", required: false },
    { name: "gender", required: false },
    { name: "pronouns", required: false },
    { name: "aptUnit", required: false },
    { name: "streetNumber", required: false },
    { name: "streetName", required: false },
    { name: "city", required: false },
    { name: "country", required: false },
    { name: "state", required: false },
    { name: "zipCode", required: false },
  ];

  const clientInputFields = [
    { name: "firstName", required: true },
    { name: "lastName", required: true },
    { name: "dateOfBirth", required: true },
    { name: "email", required: true },
    { name: "insuranceId", required: false },
    { name: "gender", required: false },
    { name: "pronouns", required: false },
    { name: "aptUnit", required: false },
    { name: "streetNumber", required: false },
    { name: "streetName", required: false },
    { name: "city", required: false },
    { name: "country", required: false },
    { name: "state", required: false },
    { name: "zipCode", required: false },
  ];

  const handlePhoneNumberChange = (index, type, value, setPhoneNumbers) => {
    const formattedValue = type === "number" ? formatPhoneNumber(value) : value;
    setPhoneNumbers((prev) =>
      prev.map((phoneNumber, i) =>
        i === index
          ? { ...phoneNumber, [type]: formattedValue || null }
          : phoneNumber
      )
    );
  };

  const addPhoneNumberField = (setPhoneNumbers) => {
    setPhoneNumbers((prev) => [...prev, { type: "", number: "" }]);
  };

  const removePhoneNumberField = (index, setPhoneNumbers) => {
    setPhoneNumbers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEmailChange = (index, field, value, setEmailList) => {
    setEmailList((prev) =>
      prev.map((emailObj, i) =>
        i === index ? { ...emailObj, [field]: value } : emailObj
      )
    );
  };

  const addEmailField = (setEmailList) => {
    setEmailList((prev) => [...prev, { label: "", email: "" }]);
  };

  const removeEmailField = (index, setEmailList) => {
    setEmailList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleParentSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    if (!validateForm(formData)) {
      const firstError = document.querySelector(".error-field");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Construct parent data from form inputs
    const parentData = {
      firstName: formData.get("parentFirstName"),
      lastName: formData.get("parentLastName"),
      dateOfBirth: formData.get("parentDateOfBirth"),
      email: formData.get("parentEmail"),
      phoneNumbers: parentPhoneNumbers,
      address: {
        aptUnit: formData.get("parentAptUnit"),
        streetNumber: formData.get("parentStreetNumber"),
        streetName: formData.get("parentStreetName"),
        city: formData.get("parentCity"),
        state: formData.get("parentState"),
        zipCode: formData.get("parentZipCode"),
      },
    };

    try {
      console.log("Dispatching createParent with data:", parentData);
      const result = await dispatch(createParent(parentData)).unwrap();
      console.log("Parent created successfully:", result);
      setSuccessState({
        type: "parent",
        id: result.parent.clientId,
      });
    } catch (error) {
      console.error("Error saving parent:", error);
      setErrors({
        submit: "An unexpected error occurred. Please try again.",
      });
      toast.error("Failed to create parent. Please try again.");
    }
  };

  // Add this helper function for required field labels
  const getRequiredFieldLabel = (label) => (
    <div className="flex justify-between">
      <span className="block text-sm font-medium leading-6 text-gray-900">
        {label}
      </span>
      <span className="text-sm text-red-600">*Required</span>
    </div>
  );

  // Add this helper function for field error message
  const getFieldErrorMessage = (fieldName) => {
    if (errors[fieldName]) {
      return <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>;
    }
    return null;
  };

  if (successState) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 overflow-y-auto">
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="bg-white rounded-sm p-6 max-w-md w-full mx-4 relative z-10 shadow-lg">
          <div className="flex flex-col items-center text-center">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {successState.type === "client" ? "Client" : "Parent"} created
              successfully - ID {successState.id}
            </h3>
            <button
              onClick={onClose}
              className="mt-6 w-full rounded-sm bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={modalRef}
        className="bg-white rounded-sm p-6 max-w-4xl w-full mx-4 relative z-10 overflow-y-auto max-h-[90vh] shadow-lg">
        <form
          ref={formRef}
          onSubmit={addParent ? handleParentSubmit : handleSave}>
          {/* Header */}
          <div className="border-b border-gray-200 pb-4 mb-4 relative">
            <button onClick={onClose} className="absolute top-0 right-0">
              <XMarkIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
            </button>
            <h2 className="text-lg font-semibold leading-7 text-gray-900">
              {addParent ? "Quick Add Parent" : "Quick Add Client"}
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              {addParent
                ? "Use this form to quickly add a parent with basic information."
                : "Use this form to quickly add a client-parent pair with basic information."}
            </p>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              * Required fields
            </p>
          </div>

          {/* Parent/Guardian Information */}
          <div className="border-b border-gray-200 pb-6 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Parent/Guardian Information
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-500">
                  Input the parent/guardian information of the client.
                </p>
              </div>
              <Switch
                checked={showParentInfo}
                onChange={setShowParentInfo}
                className={classNames(
                  showParentInfo ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}>
                <span className="sr-only">Use setting</span>
                <span
                  aria-hidden="true"
                  className={classNames(
                    showParentInfo ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>

            {showParentInfo && (
              <div>
                <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-4">
                  <div className="sm:col-span-4">
                    <GoogleMapsAutocomplete
                      onPlaceSelected={(place) =>
                        handlePlaceSelected(place, setParentAddress)
                      }
                      inputId="parent-autocomplete"
                    />
                  </div>
                  {inputFields.map((field) => (
                    <div key={`parent-${field.name}`} className="sm:col-span-1">
                      <label
                        htmlFor={`parent${
                          field.name.charAt(0).toUpperCase() +
                          field.name.slice(1)
                        }`}
                        className="block text-sm font-medium leading-6 text-gray-900">
                        {field.name
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                        {field.required && " *"}
                      </label>
                      <div className="mt-2">
                        {field.name === "country" ? (
                          <select
                            name="parentCountry"
                            id="parentCountry"
                            value={parentAddress.country || "United States"}
                            onChange={(e) => {
                              const newCountry = e.target.value;
                              setParentAddress((prev) => ({
                                ...prev,
                                country: newCountry,
                                // Clear state when country changes
                                state: prev.country !== newCountry ? "" : prev.state,
                              }));
                            }}
                            className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          >
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                          </select>
                        ) : field.name === "state" ? (
                          <ProvinceSelector
                            value={parentAddress.state}
                            country={parentAddress.country}
                            onChange={(e) =>
                              setParentAddress((prev) => ({
                                ...prev,
                                state: e.target.value,
                              }))
                            }
                          />
                        ) : field.name === "gender" ? (
                          <GenderSelector
                            value={parentAddress[field.name]}
                            onChange={(e) =>
                              setParentAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                          />
                        ) : field.name === "pronouns" ? (
                          <PronounSelector
                            value={parentAddress[field.name]}
                            onChange={(e) =>
                              setParentAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          <input
                            type={
                              field.name === "dateOfBirth" ? "date" : "text"
                            }
                            name={`parent${
                              field.name.charAt(0).toUpperCase() +
                              field.name.slice(1)
                            }`}
                            id={`parent${
                              field.name.charAt(0).toUpperCase() +
                              field.name.slice(1)
                            }`}
                            value={parentAddress[field.name]}
                            onChange={(e) =>
                              setParentAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                            className={classNames(
                              "block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                              errors[`parent${field.name}`]
                                ? "error-field ring-red-300"
                                : ""
                            )}
                          />
                        )}
                        {errors[`parent${field.name}`] && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors[`parent${field.name}`]}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="sm:col-span-4">
                    <h3 className="text-base font-semibold leading-7 text-gray-900">
                      Additional Emails
                    </h3>
                    {parentEmails.map((emailField, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          placeholder="Label (e.g., Work)"
                          value={emailField.label}
                          onChange={(e) =>
                            handleEmailChange(
                              index,
                              "label",
                              e.target.value,
                              setParentEmails
                            )
                          }
                          className="block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={emailField.email}
                          onChange={(e) =>
                            handleEmailChange(
                              index,
                              "email",
                              e.target.value,
                              setParentEmails
                            )
                          }
                          className="block w-2/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            removeEmailField(index, setParentEmails)
                          }
                          className="rounded-sm bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 shadow-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addEmailField(setParentEmails)}
                      className="text-sm font-semibold leading-6 text-gray-900">
                      Add Additional Email
                    </button>
                  </div>

                  <div className="sm:col-span-4">
                    <h3 className="text-base font-semibold leading-7 text-gray-900">
                      Phone Numbers *
                    </h3>
                    {errors.parentPhoneNumbers && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.parentPhoneNumbers}
                      </p>
                    )}
                    {parentPhoneNumbers.map((phoneNumber, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        {/* Dropdown for Phone Type */}
                        <select
                          value={phoneNumber.type}
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "type",
                              e.target.value,
                              setParentPhoneNumbers
                            )
                          }
                          className={classNames(
                            "block w-1/4 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                            errors.parentPhoneNumbers
                              ? "error-field ring-red-300"
                              : ""
                          )}>
                          <option value="">Select Type</option>
                          <option value="Home">Home</option>
                          <option value="Work">Work</option>
                          <option value="Mobile">Mobile</option>
                          <option value="Fax">Fax</option>
                          <option value="Other">Other</option>
                        </select>

                        {/* Phone Number Input */}
                        <input
                          type="text"
                          value={phoneNumber.number}
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "number",
                              e.target.value,
                              setParentPhoneNumbers
                            )
                          }
                          className={classNames(
                            "block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                            errors.parentPhoneNumbers
                              ? "error-field ring-red-300"
                              : ""
                          )}
                        />

                        {/* Notes Field (Optional) */}
                        <input
                          type="text"
                          value={phoneNumber.note || ""}
                          placeholder="Notes (Optional)"
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "note",
                              e.target.value,
                              setParentPhoneNumbers
                            )
                          }
                          className="block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() =>
                            removePhoneNumberField(index, setParentPhoneNumbers)
                          }
                          className="rounded-sm bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 shadow-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addPhoneNumberField(setParentPhoneNumbers)}
                      className="text-sm font-semibold leading-6 text-gray-900">
                      Add Phone Number
                    </button>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    Second Parent Information
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Input the second parent's information if available.
                  </p>

                  <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-4">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="secondParentEmail"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Second Parent Email
                      </label>
                      <div className="mt-2">
                        <input
                          type="email"
                          name="secondParentEmail"
                          id="secondParentEmail"
                          value={secondParentInfo.email}
                          onChange={(e) =>
                            setSecondParentInfo((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="secondParentPhone"
                        className="block text-sm font-medium leading-6 text-gray-900">
                        Second Parent Phone
                      </label>
                      <div className="mt-2">
                        <input
                          type="text"
                          name="secondParentPhone"
                          id="secondParentPhone"
                          value={secondParentInfo.phone}
                          onChange={(e) =>
                            setSecondParentInfo((prev) => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                          className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {!addParent && (
            <>
              {/* Client Information */}
              <div className="mt-8 border-b border-gray-900/10 pb-12">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  Client Information
                </h2>
                <p className="mt-1 text-sm leading-6 text-gray-600">
                  Input the information of the client.
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-4">
                  <div className="sm:col-span-4">
                    <GoogleMapsAutocomplete
                      onPlaceSelected={(place) =>
                        handlePlaceSelected(place, setClientAddress)
                      }
                      inputId="client-autocomplete"
                    />
                  </div>
                  {clientInputFields.map((field) => (
                    <div key={`client-${field.name}`} className="sm:col-span-1">
                      <label
                        htmlFor={`client${
                          field.name.charAt(0).toUpperCase() +
                          field.name.slice(1)
                        }`}
                        className="block text-sm font-medium leading-6 text-gray-900">
                        {field.name === "email"
                          ? "Primary Email"
                          : field.name
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())}
                        {field.required && " *"}
                      </label>
                      <div className="mt-2">
                        {field.name === "country" ? (
                          <select
                            name="clientCountry"
                            id="clientCountry"
                            value={clientAddress.country || "United States"}
                            onChange={(e) => {
                              const newCountry = e.target.value;
                              setClientAddress((prev) => ({
                                ...prev,
                                country: newCountry,
                                // Clear state when country changes
                                state: prev.country !== newCountry ? "" : prev.state,
                              }));
                            }}
                            className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          >
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                          </select>
                        ) : field.name === "state" ? (
                          <ProvinceSelector
                            value={clientAddress.state}
                            country={clientAddress.country}
                            onChange={(e) =>
                              setClientAddress((prev) => ({
                                ...prev,
                                state: e.target.value,
                              }))
                            }
                          />
                        ) : field.name === "gender" ? (
                          <GenderSelector
                            value={clientAddress[field.name]}
                            onChange={(e) =>
                              setClientAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                          />
                        ) : field.name === "pronouns" ? (
                          <PronounSelector
                            value={clientAddress[field.name]}
                            onChange={(e) =>
                              setClientAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                          />
                        ) : (
                          <input
                            type={
                              field.name === "dateOfBirth" ? "date" : "text"
                            }
                            name={`client${
                              field.name.charAt(0).toUpperCase() +
                              field.name.slice(1)
                            }`}
                            id={`client${
                              field.name.charAt(0).toUpperCase() +
                              field.name.slice(1)
                            }`}
                            value={clientAddress[field.name]}
                            onChange={(e) =>
                              setClientAddress((prev) => ({
                                ...prev,
                                [field.name]: e.target.value,
                              }))
                            }
                            className={classNames(
                              "block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                              errors[`client${field.name}`]
                                ? "error-field ring-red-300"
                                : ""
                            )}
                          />
                        )}
                        {getFieldErrorMessage(`client${field.name}`)}
                      </div>
                    </div>
                  ))}
                  <div className="sm:col-span-4">
                    <h3 className="text-base font-semibold leading-7 text-gray-900">
                      Additional Emails
                    </h3>
                    {clientEmails.map((emailField, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          placeholder="Label (e.g., Work)"
                          value={emailField.label}
                          onChange={(e) =>
                            handleEmailChange(
                              index,
                              "label",
                              e.target.value,
                              setClientEmails
                            )
                          }
                          className="block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                        <input
                          type="email"
                          placeholder="Email Address"
                          value={emailField.email}
                          onChange={(e) =>
                            handleEmailChange(
                              index,
                              "email",
                              e.target.value,
                              setClientEmails
                            )
                          }
                          className="block w-2/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            removeEmailField(index, setClientEmails)
                          }
                          className="rounded-sm bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 shadow-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addEmailField(setClientEmails)}
                      className="text-sm font-semibold leading-6 text-gray-900">
                      Add Additional Email
                    </button>
                  </div>

                  <div className="sm:col-span-4">
                    <h3 className="text-base font-semibold leading-7 text-gray-900">
                      Phone Numbers *
                    </h3>
                    {/* {errors.clientPhoneNumbers && (
                      <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded error-field">
                        {errors.clientPhoneNumbers}
                      </div>
                    )} */}
                    {clientPhoneNumbers.map((phoneNumber, index) => (
                      <div key={index} className="flex space-x-2 mb-2">
                        {/* Dropdown for Phone Type */}
                        <select
                          value={phoneNumber.type}
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "type",
                              e.target.value,
                              setClientPhoneNumbers
                            )
                          }
                          className={`block w-1/4 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                            errors.clientPhoneNumbers
                              ? "ring-red-300"
                              : "ring-gray-300"
                          } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}>
                          <option value="">Select Type</option>
                          <option value="Home">Home</option>
                          <option value="Work">Work</option>
                          <option value="Mobile">Mobile</option>
                          <option value="Fax">Fax</option>
                          <option value="Other">Other</option>
                        </select>

                        {/* Phone Number Input */}
                        <input
                          type="text"
                          value={phoneNumber.number}
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "number",
                              e.target.value,
                              setClientPhoneNumbers
                            )
                          }
                          className={`block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                            errors.clientPhoneNumbers
                              ? "ring-red-300"
                              : "ring-gray-300"
                          } focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`}
                        />

                        {/* Notes Field (Optional) */}
                        <input
                          type="text"
                          value={phoneNumber.note || ""}
                          placeholder="Notes (Optional)"
                          onChange={(e) =>
                            handlePhoneNumberChange(
                              index,
                              "note",
                              e.target.value,
                              setClientPhoneNumbers
                            )
                          }
                          className="block w-1/3 rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() =>
                            removePhoneNumberField(index, setClientPhoneNumbers)
                          }
                          className="rounded-sm bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 shadow-sm">
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addPhoneNumberField(setClientPhoneNumbers)}
                      className="text-sm font-semibold leading-6 text-gray-900">
                      Add Phone Number
                    </button>
                  </div>
                </div>
              </div>

              {/* Treating and Supervising Clinician */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 border-b border-gray-900/10 pb-12">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">
                    Treating clinician:
                  </div>
                  <SearchDropdown
                    prefix=":user:"
                    displayFields={["firstName", "lastName", "email"]}
                    onSelection={(user) => setTreatingClinician(user)}
                    placeholder={"Search for a treating clinician"}
                  />
                </div>

                {treatingClinician && (
                  <div className="mt-4 p-4 mb-4 border border-gray-300 rounded-sm flex items-center">
                    <h3 className="text-sm font-semibold leading-7 text-gray-900 mr-4">
                      Treating Clinician:
                    </h3>
                    <p className="text-sm leading-6 text-gray-600">
                      {treatingClinician.firstName} {treatingClinician.lastName}
                      ,{treatingClinician.specialization}
                    </p>
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">
                    Supervising clinician:
                  </div>
                  <SearchDropdown
                    prefix=":user:"
                    displayFields={["firstName", "lastName", "email"]}
                    onSelection={(user) => setSupervisingClinician(user)}
                    placeholder={"Search for a supervising clinician"}
                  />
                </div>

                {supervisingClinician && (
                  <div className="mt-4 p-4 mb-4 border border-gray-300 rounded-sm flex items-center">
                    <h3 className="text-sm font-semibold leading-7 text-gray-900 mr-4">
                      Supervising Clinician:
                    </h3>
                    <p className="text-sm leading-6 text-gray-600">
                      {supervisingClinician.firstName}{" "}
                      {supervisingClinician.lastName},{" "}
                      {supervisingClinician.specialization}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Save and Cancel Buttons */}
          <div className="mt-6 flex items-center justify-end gap-x-4">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium leading-6 text-gray-700 hover:text-gray-900">
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Save
            </button>
          </div>

          {/* {!addParent && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-sm text-sm">
              <p className="font-medium">Required Information</p>
              <ul className="mt-1 list-disc list-inside text-sm">
                <li>Client's first and last name</li>
                <li>Client's date of birth</li>
                <li>Client's email address</li>
                <li>At least one phone number with type</li>
              </ul>
            </div>
          )} */}

          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-sm text-sm">
              {errors.submit}
            </div>
          )}

          {/* Show validation errors at the bottom */}
          {getValidationErrors()}
        </form>
      </div>
    </div>
  );
}
